import { skipToken } from "@reduxjs/toolkit/query/react";
import useGetCurrentUserDocument from "./useGetCurrentUserDocument";
import {
  EventStatus,
  ProductType,
  EventFilter,
  BookingStatus,
  PaymentStatus,
} from "../../../api/src/types";
import { useGetPurchaseItemsWithEventsQuery } from "../api/payments";
import { isAfter, isBefore } from "date-fns";

export const useHasEvent = (
  productType: ProductType | null = null,
  filter: EventFilter | null = null,
  eventStatus: EventStatus | null = null,
) => {
  const {
    user,
    document,
    isLoading: isUserLoading,
    error: userError,
  } = useGetCurrentUserDocument();

  const {
    data: allPurchaseItemsWithEvents,
    isLoading: isPurchaseItemsWithEventsLoading,
    error: isPurchaseItemsWithEventsError,
  } = useGetPurchaseItemsWithEventsQuery(
    user?.id ? { id: user.id, filter: EventFilter.ALL } : skipToken,
  );
  const purchaseItems = allPurchaseItemsWithEvents?.filter((item) =>
    productType ? item.product.productType === productType : true,
  );

  const events = purchaseItems?.flatMap((purchaseItem) =>
    purchaseItem.events.filter(
      (event) =>
        (eventStatus ? event.eventStatus === eventStatus : true) &&
        (filter === EventFilter.FUTURE
          ? isAfter(event.eventStartAt, new Date())
          : true) &&
        (filter === EventFilter.PAST
          ? isBefore(event.eventEndAt, new Date())
          : true),
    ),
  );

  const isLoading = isUserLoading || isPurchaseItemsWithEventsLoading;
  const isError = userError ?? isPurchaseItemsWithEventsError;

  const hasEvent = (events?.length ?? 0) > 0;

  const purchasedRepresentations = allPurchaseItemsWithEvents?.filter(
    (item) => item.product.productType === ProductType.REPRESENTATION,
  );
  const futureBookedRepresentationEvents = purchasedRepresentations?.flatMap(
    (purchaseItem) =>
      purchaseItem.events.filter(
        (event) =>
          event.eventStatus === EventStatus.BOOKED &&
          isAfter(event.eventStartAt, new Date()),
      ),
  );
  const hasScheduledRepresentation = !!document?.purchaseItems.some(
    (purchaseItem) =>
      purchaseItem.paymentStatus === PaymentStatus.SUCCESSFUL &&
      purchaseItem.bookingStatus === BookingStatus.SCHEDULED &&
      purchaseItem.product.productType === ProductType.REPRESENTATION,
  );

  const blockedByFutureRepresentationEvent =
    !!isLoading ||
    !!isError ||
    (futureBookedRepresentationEvents?.length ?? 0) > 0 ||
    hasScheduledRepresentation;

  return {
    hasEvent,
    blockedByFutureRepresentationEvent,
    hasScheduledRepresentation,
    isLoading,
    isError,
    events,
    purchaseItems,
  };
};
